<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="900"
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex align-center pa-3 pb-2">
        <v-icon size="20" color="accent" class="mr-2">mdi-cog</v-icon>
        <span class="text-body-1 font-weight-bold">Manage Task Types</span>
        <v-spacer />
        <v-btn size="small" color="accent" variant="flat" class="mr-2" @click="openAddDialog">
          <v-icon start size="16">mdi-plus</v-icon>
          Add Type
        </v-btn>
        <v-btn icon size="x-small" variant="text" @click="$emit('update:modelValue', false)">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <div class="pa-3 pb-0 d-flex align-center ga-2">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search task types..."
          variant="outlined"
          density="compact"
          hide-details
          prepend-inner-icon="mdi-magnify"
          clearable
          class="flex-grow-1"
        />
        <v-btn-toggle v-model="statusFilter" density="compact" color="accent" variant="outlined" mandatory>
          <v-btn value="active" size="small">Active</v-btn>
          <v-btn value="inactive" size="small">Hidden</v-btn>
          <v-btn value="all" size="small">All</v-btn>
        </v-btn-toggle>
        <v-btn-toggle v-model="slotFilter" density="compact" color="accent" variant="outlined" mandatory>
          <v-btn value="all" size="small">All</v-btn>
          <v-btn value="slot" size="small">
            <v-icon start size="16">mdi-calendar-clock</v-icon>Slot
          </v-btn>
          <v-btn value="noslot" size="small">No slot</v-btn>
        </v-btn-toggle>
      </div>

      <v-card-text class="pa-0 pt-2" style="max-height: 55vh;">
        <v-table density="compact" hover class="tt-table">
          <thead>
            <tr>
              <th style="width: 34%;">Name</th>
              <th style="width: 16%;">Category</th>
              <th style="width: 70px;" class="text-center">Color</th>
              <th style="width: 64px;" class="text-center">Icon</th>
              <th style="width: 110px;" class="text-center">Slot</th>
              <th style="width: 96px;" class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tt in filteredTypes" :key="tt.id" :class="{ 'tt-row-inactive': !tt.is_active }">
              <td class="text-body-2">
                <div class="d-flex align-center ga-2 py-1 tt-name-cell">
                  <v-icon v-if="tt.icon" :icon="tt.icon" size="18" :color="swatchColor(tt.color)" class="flex-shrink-0" />
                  <span
                    class="text-truncate"
                    :class="{ 'text-medium-emphasis text-decoration-line-through': !tt.is_active }"
                    :title="tt.name"
                  >{{ tt.name }}</span>
                  <v-chip v-if="tt.is_system" size="x-small" color="info" variant="tonal" class="flex-shrink-0">system</v-chip>
                </div>
              </td>
              <td class="text-body-2">
                <v-chip size="x-small" variant="tonal">{{ tt.category || 'general' }}</v-chip>
              </td>
              <td class="text-center">
                <!-- Single current-color swatch; click opens palette + custom picker -->
                <v-menu :close-on-content-click="false" location="bottom start">
                  <template #activator="{ props: mp }">
                    <div
                      v-bind="mp"
                      class="color-swatch color-swatch-current mx-auto"
                      :style="{ backgroundColor: swatchColor(tt.color) }"
                    >
                      <v-icon v-if="!tt.color" size="12" class="text-medium-emphasis">mdi-eyedropper</v-icon>
                    </div>
                  </template>
                  <v-card width="248" class="pa-3">
                    <div class="text-caption font-weight-bold mb-2">Color</div>
                    <div class="d-flex flex-wrap ga-1 mb-2">
                      <div
                        v-for="c in colorPalette"
                        :key="c"
                        class="color-swatch"
                        :class="{ 'color-swatch-active': tt.color === c }"
                        :style="{ backgroundColor: swatchColor(c) }"
                        @click="setField(tt, 'color', c)"
                      />
                    </div>
                    <v-divider class="mb-2" />
                    <div class="text-caption mb-1">Custom</div>
                    <input
                      type="color"
                      :value="tt.color?.startsWith('#') ? tt.color : undefined"
                      class="custom-color-picker"
                      @input="setField(tt, 'color', $event.target.value)"
                    />
                    <v-text-field
                      :model-value="tt.color || ''"
                      placeholder="#HEX"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="mt-2"
                      maxlength="7"
                      @update:model-value="onHexInput(tt, $event)"
                    />
                  </v-card>
                </v-menu>
              </td>
              <td class="text-center">
                <v-menu :close-on-content-click="false" location="bottom start">
                  <template #activator="{ props: mp }">
                    <v-btn v-bind="mp" icon size="x-small" variant="outlined" density="comfortable">
                      <v-icon :icon="tt.icon || 'mdi-pencil-plus'" size="18" />
                      <v-tooltip activator="parent" location="top">
                        {{ tt.icon ? tt.icon.replace('mdi-', '') : 'Choose icon' }}
                      </v-tooltip>
                    </v-btn>
                  </template>
                  <v-card width="280">
                    <v-card-text class="pa-2">
                      <v-text-field
                        v-model="iconSearch"
                        placeholder="Search icons..."
                        variant="outlined"
                        density="compact"
                        hide-details
                        prepend-inner-icon="mdi-magnify"
                        class="mb-2"
                        autofocus
                      />
                      <div class="icon-grid">
                        <div
                          v-for="ic in filteredIcons"
                          :key="ic"
                          class="icon-item"
                          :class="{ 'icon-item-active': tt.icon === ic }"
                          @click="setField(tt, 'icon', ic)"
                        >
                          <v-icon :icon="ic" size="20" />
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </td>
              <td class="text-center">
                <v-chip
                  v-if="tt.occupies_timeslot"
                  size="x-small"
                  color="accent"
                  variant="tonal"
                  prepend-icon="mdi-calendar-clock"
                >
                  {{ (tt.slot_minutes || tt.sla_minutes || 60) }}m
                </v-chip>
                <span v-else class="text-medium-emphasis text-caption">—</span>
              </td>
              <td class="text-center">
                <v-btn icon size="x-small" variant="text" color="primary" @click="openEditDialog(tt)">
                  <v-icon size="16">mdi-pencil</v-icon>
                  <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                </v-btn>
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  :color="tt.is_active ? 'error' : 'success'"
                  @click="toggleActive(tt)"
                >
                  <v-icon size="16">{{ tt.is_active ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  <v-tooltip activator="parent" location="top">
                    {{ tt.is_active ? 'Deactivate (hide from Quick Task)' : 'Activate' }}
                  </v-tooltip>
                </v-btn>
              </td>
            </tr>
            <tr v-if="!filteredTypes.length">
              <td colspan="6" class="text-center text-medium-emphasis pa-4">No task types found</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <span v-if="pendingCount" class="text-caption text-warning">
          {{ pendingCount }} unsaved change{{ pendingCount > 1 ? 's' : '' }}
        </span>
        <v-spacer />
        <v-btn variant="text" size="small" @click="$emit('update:modelValue', false)">Close</v-btn>
        <v-btn
          color="accent"
          variant="flat"
          size="small"
          :loading="saving"
          :disabled="!pendingCount"
          @click="saveAll"
        >
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Add / Edit sub-dialog -->
    <v-dialog v-model="showEditDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="pa-3 pb-2 text-body-1 font-weight-bold">
          {{ editForm.id ? 'Edit Task Type' : 'New Task Type' }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-3">
          <v-text-field
            v-model="editForm.name"
            label="Name"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Required']"
            autofocus
            class="mb-2"
          />
          <v-select
            v-model="editForm.category"
            :items="categories"
            label="Category"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-2"
          />
          <v-select
            v-model="editForm.default_priority"
            :items="priorities"
            label="Default Priority"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-2"
          />
          <v-text-field
            v-model.number="editForm.sla_minutes"
            label="SLA (minutes)"
            variant="outlined"
            density="compact"
            type="number"
            hide-details
            class="mb-2"
          />

          <!-- Occupies a schedule slot (per-type default) + slot length -->
          <div class="d-flex align-center justify-space-between mb-2 pa-2"
               style="border: 1px solid rgba(var(--v-theme-on-surface), 0.12); border-radius: 8px;">
            <div class="d-flex align-center ga-2">
              <v-icon
                size="20"
                :color="editForm.occupies_timeslot ? 'accent' : undefined"
                :class="{ 'text-medium-emphasis': !editForm.occupies_timeslot }"
              >mdi-calendar-clock</v-icon>
              <div>
                <div class="text-body-2 font-weight-medium">Occupies a schedule slot</div>
                <div class="text-caption text-medium-emphasis">
                  {{ editForm.occupies_timeslot
                    ? 'Shows on the schedule (dispatcher / week / technician) like a job'
                    : 'Reminder only — not shown on the schedule' }}
                </div>
              </div>
            </div>
            <v-switch v-model="editForm.occupies_timeslot" color="accent" density="compact" hide-details inset />
          </div>
          <v-text-field
            v-if="editForm.occupies_timeslot"
            v-model.number="editForm.slot_minutes"
            label="Slot length (minutes)"
            variant="outlined"
            density="compact"
            type="number"
            min="5"
            step="5"
            hint="Default block length on the schedule. Empty = use SLA, else 60 min."
            persistent-hint
            class="mb-2"
          />

          <v-text-field
            v-model="editForm.description"
            label="Description (optional)"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3 pt-2">
          <v-spacer />
          <v-btn variant="text" size="small" @click="showEditDialog = false">Cancel</v-btn>
          <v-btn
            color="accent"
            variant="flat"
            size="small"
            :loading="editSaving"
            :disabled="!editForm.name"
            @click="saveEditForm"
          >
            {{ editForm.id ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { taskTypesApi } from '@/api/tasks'
import { useNotify } from '@/composables/useNotify'
import { resolveThemeTokenColor } from '@/utils/color'

const props = defineProps({ modelValue: Boolean })
const emit = defineEmits(['update:modelValue', 'saved'])
const { notify } = useNotify()

const taskTypes = ref([])
const originalMap = ref({})
const saving = ref(false)
const editSaving = ref(false)
const searchQuery = ref('')
const slotFilter = ref('all')
// Default to "active" so the manage list is clean (deactivated/hidden types are
// out of the way). Switch to Hidden/All to reactivate or review them.
const statusFilter = ref('active')
const iconSearch = ref('')
const showEditDialog = ref(false)

const editForm = ref({
  id: null,
  name: '',
  code: '',
  category: 'general',
  default_priority: 'medium',
  sla_minutes: null,
  description: '',
  occupies_timeslot: false,
  slot_minutes: null,
})

const categories = ['general', 'dispatch', 'sales', 'parts', 'complaint', 'technician']
const priorities = ['low', 'medium', 'high', 'critical']

const colorPalette = [
  'error', 'warning', 'success', 'accent', 'info', 'secondary', 'primary',
]

function swatchColor(c) {
  return resolveThemeTokenColor(c, 'rgb(var(--v-theme-surface-variant))')
}

const availableIcons = [
  'mdi-clipboard-text', 'mdi-clipboard-check', 'mdi-clipboard-alert',
  'mdi-phone-return', 'mdi-phone-outgoing', 'mdi-phone-incoming',
  'mdi-cash-multiple', 'mdi-currency-usd', 'mdi-receipt',
  'mdi-package-variant', 'mdi-cog-outline', 'mdi-tools',
  'mdi-wrench', 'mdi-hammer', 'mdi-screwdriver',
  'mdi-alert-circle', 'mdi-alert-outline', 'mdi-bell-ring',
  'mdi-calendar-clock', 'mdi-calendar-edit', 'mdi-calendar-check',
  'mdi-account-alert', 'mdi-account-clock', 'mdi-account-check',
  'mdi-email-outline', 'mdi-email-send', 'mdi-send',
  'mdi-file-document-outline', 'mdi-file-send', 'mdi-file-check',
  'mdi-star-outline', 'mdi-thumb-up', 'mdi-thumb-down',
  'mdi-shield-check', 'mdi-shield-alert', 'mdi-run-fast',
  'mdi-clock-alert', 'mdi-timer-sand', 'mdi-bookmark',
  'mdi-heart-pulse', 'mdi-lightbulb', 'mdi-flag',
]

const filteredIcons = computed(() => {
  if (!iconSearch.value) return availableIcons
  const q = iconSearch.value.toLowerCase()
  return availableIcons.filter(ic => ic.toLowerCase().includes(q))
})

const filteredTypes = computed(() => {
  let list = [...taskTypes.value]
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(t => t.name.toLowerCase().includes(q) || (t.code || '').toLowerCase().includes(q))
  }
  if (statusFilter.value === 'active') {
    list = list.filter(t => !!t.is_active)
  } else if (statusFilter.value === 'inactive') {
    list = list.filter(t => !t.is_active)
  }
  if (slotFilter.value === 'slot') {
    list = list.filter(t => !!t.occupies_timeslot)
  } else if (slotFilter.value === 'noslot') {
    list = list.filter(t => !t.occupies_timeslot)
  }
  list.sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1
    return (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)
  })
  return list
})

const pendingCount = computed(() => {
  return taskTypes.value.filter(tt => {
    const orig = originalMap.value[tt.id]
    if (!orig) return false
    return tt.color !== orig.color || tt.icon !== orig.icon
  }).length
})

function setField(tt, field, value) {
  tt[field] = value
}

function onHexInput(tt, val) {
  if (!val) { tt.color = null; return }
  const clean = val.startsWith('#') ? val : '#' + val
  if (/^#[0-9a-fA-F]{6}$/.test(clean)) tt.color = clean
}

async function loadTaskTypes() {
  try {
    const { data } = await taskTypesApi.list({ active_only: false })
    const items = Array.isArray(data) ? data : (data.results || [])
    taskTypes.value = items.map(t => ({ ...t }))
    originalMap.value = {}
    items.forEach(t => { originalMap.value[t.id] = { color: t.color, icon: t.icon } })
    console.log('[TaskTypeSettings] Loaded', items.length, 'types')
  } catch (e) {
    console.error('[TaskTypeSettings] Load error:', e)
    notify.error('Failed to load task types')
  }
}

async function saveAll() {
  saving.value = true
  let saved = 0
  try {
    for (const tt of taskTypes.value) {
      const orig = originalMap.value[tt.id]
      if (!orig) continue
      if (tt.color !== orig.color || tt.icon !== orig.icon) {
        await taskTypesApi.update(tt.id, { color: tt.color, icon: tt.icon })
        originalMap.value[tt.id] = { color: tt.color, icon: tt.icon }
        saved++
      }
    }
    notify.success(`Saved ${saved} task type(s)`)
    emit('saved')
  } catch (e) {
    console.error('[TaskTypeSettings] Save error:', e)
    notify.error('Failed to save changes')
  } finally {
    saving.value = false
  }
}

async function toggleActive(tt) {
  try {
    await taskTypesApi.update(tt.id, { is_active: !tt.is_active })
    tt.is_active = !tt.is_active
    notify.success(tt.is_active ? `"${tt.name}" activated` : `"${tt.name}" deactivated`)
    emit('saved')
  } catch (e) {
    console.error('[TaskTypeSettings] Toggle error:', e)
    notify.error('Failed to update status')
  }
}

function openAddDialog() {
  editForm.value = {
    id: null, name: '', code: '', category: 'general',
    default_priority: 'medium', sla_minutes: null, description: '',
    occupies_timeslot: false, slot_minutes: null,
  }
  showEditDialog.value = true
}

function openEditDialog(tt) {
  editForm.value = {
    id: tt.id, name: tt.name, code: tt.code, category: tt.category || 'general',
    default_priority: tt.default_priority || 'medium',
    sla_minutes: tt.sla_minutes, description: tt.description || '',
    occupies_timeslot: !!tt.occupies_timeslot,
    slot_minutes: tt.slot_minutes ?? null,
  }
  showEditDialog.value = true
}

async function saveEditForm() {
  editSaving.value = true
  try {
    const slotMinutes = editForm.value.occupies_timeslot
      ? (editForm.value.slot_minutes || null)
      : null
    if (editForm.value.id) {
      await taskTypesApi.update(editForm.value.id, {
        name: editForm.value.name,
        category: editForm.value.category,
        default_priority: editForm.value.default_priority,
        sla_minutes: editForm.value.sla_minutes,
        description: editForm.value.description,
        occupies_timeslot: editForm.value.occupies_timeslot,
        slot_minutes: slotMinutes,
      })
      notify.success(`"${editForm.value.name}" updated`)
    } else {
      await taskTypesApi.create({
        name: editForm.value.name,
        category: editForm.value.category,
        default_priority: editForm.value.default_priority,
        sla_minutes: editForm.value.sla_minutes,
        description: editForm.value.description,
        occupies_timeslot: editForm.value.occupies_timeslot,
        slot_minutes: slotMinutes,
      })
      notify.success(`"${editForm.value.name}" created`)
    }
    showEditDialog.value = false
    await loadTaskTypes()
    emit('saved')
  } catch (e) {
    console.error('[TaskTypeSettings] Save form error:', e)
    const msg = e.response?.data?.error || 'Failed to save'
    notify.error(msg)
  } finally {
    editSaving.value = false
  }
}

watch(() => props.modelValue, (val) => {
  if (val) {
    searchQuery.value = ''
    iconSearch.value = ''
    statusFilter.value = 'active'
    slotFilter.value = 'all'
    loadTaskTypes()
  }
})
</script>

<style scoped>
/* Fixed layout so columns keep their width and long names truncate cleanly
   instead of wrapping and pushing the Actions column out of view. */
.tt-table :deep(table) { table-layout: fixed; width: 100%; }
.tt-table :deep(td), .tt-table :deep(th) {
  overflow: hidden;
  text-overflow: ellipsis;
}
.tt-row-inactive { background-color: rgb(var(--v-theme-surface-variant)); }
.tt-name-cell { min-width: 0; }
.tt-name-cell .text-truncate { min-width: 0; }
.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.color-swatch:hover { transform: scale(1.15); }
.color-swatch-active {
  border-color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 0 0 1px rgba(var(--v-theme-on-surface), 0.2);
}
.color-swatch-custom {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  border-style: dashed;
  border-color: rgba(var(--v-theme-on-surface), 0.2);
}
.color-swatch-current {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.25);
}
.color-swatch-current:hover { transform: scale(1.1); }
.custom-color-picker {
  width: 100%;
  height: 36px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
}
.custom-color-picker::-webkit-color-swatch-wrapper { padding: 0; }
.custom-color-picker::-webkit-color-swatch { border: none; border-radius: 6px; }
.icon-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
}
.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: background-color 0.1s;
}
.icon-item:hover { background-color: rgba(var(--v-theme-primary), 0.08); }
.icon-item-active {
  border-color: rgb(var(--v-theme-accent));
  background-color: rgba(var(--v-theme-accent), 0.1);
}
</style>
